import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Helmet from 'react-helmet';

import Breadcrumb from '../../../container/breadcrumb';
import HeaderActions from '../../../container/header_actions';
import PeriodBreadcrumb from '../../../component/PeriodBreadcrumb';

import OverviewTab from './OverviewTab';
import LogsTab from './LogsTab';
import DatabaseTab from './DatabaseTab';
import SupportTab from './SupportTab';

import './style.scss';

interface SelfMonitoringProps {
    isAdmin: boolean;
    toggleMenu: Function;
}

type ActiveTab = 'overview' | 'logs' | 'database' | 'support';

// Admin-only screen showing DBmarlin's own health: archiver + agent
// JVM/pool/executor metrics (Overview), a live Tomcat log tail (Logs), and
// support bundle generation (Support). See docs/architecture-review.md for
// the incident this was built to make visible from the browser.
function SelfMonitoring(props: SelfMonitoringProps) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        if (!props.isAdmin) {
            navigate('/not-found');
        }
    }, [props.isAdmin]);

    const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

    return (
        <div id="content" className="self-monitoring">
            <Helmet>
                <title>Self Monitoring - Settings - DBmarlin</title>
                <meta name="description" content="DBmarlin's own JVM, connection pool, log and support diagnostics" />
            </Helmet>

            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading="Self Monitoring">
                    <Link to="/admin">Settings</Link>
                    <Link to="/admin/self-monitoring" className="current-breadcrumb-link">Self Monitoring</Link>
                    <PeriodBreadcrumb />
                </Breadcrumb>
                <HeaderActions toggleMenu={props.toggleMenu} refresh={refresh} />
            </div>

            <div className="menu-top nav nav-tabs" role="tablist">
                <a
                    role="tab"
                    className={activeTab === 'overview' ? 'active' : ''}
                    onClick={() => setActiveTab('overview')}
                    style={{ cursor: 'pointer' }}
                >
                    <i className="fal fa-gauge-high fa-fw" aria-hidden="true" />
                    Overview
                </a>
                <a
                    role="tab"
                    className={activeTab === 'logs' ? 'active' : ''}
                    onClick={() => setActiveTab('logs')}
                    style={{ cursor: 'pointer' }}
                >
                    <i className="fal fa-file-lines fa-fw" aria-hidden="true" />
                    Logs
                </a>
                <a
                    role="tab"
                    className={activeTab === 'database' ? 'active' : ''}
                    onClick={() => setActiveTab('database')}
                    style={{ cursor: 'pointer' }}
                >
                    <i className="fal fa-database fa-fw" aria-hidden="true" />
                    Database
                </a>
                <a
                    role="tab"
                    className={activeTab === 'support' ? 'active' : ''}
                    onClick={() => setActiveTab('support')}
                    style={{ cursor: 'pointer' }}
                >
                    <i className="fal fa-life-ring fa-fw" aria-hidden="true" />
                    Support
                </a>
            </div>

            <div className="row row-cols-1">
                <div className="col">
                    {activeTab === 'overview' && <OverviewTab refreshKey={refreshKey} />}
                    {activeTab === 'logs' && <LogsTab />}
                    {activeTab === 'database' && <DatabaseTab refreshKey={refreshKey} />}
                    {activeTab === 'support' && <SupportTab />}
                </div>
            </div>
        </div>
    );
}

export default SelfMonitoring;
